import type {
  CloneRiskModelRequest,
  DeleteCheckResponse,
  LocalizedString,
  RiskModel,
  RiskModelWithDetails,
  UpdateRiskModelRequest,
  UpdateRiskModelResponse,
} from "@repo/shared/types";
import { del, get, patch, post, put } from "@/lib/api/_client";

const BASE = "/internal/risk-models";
const PUBLIC_BASE = "/public/risk-models";

// --- Models ---

export function list(): Promise<RiskModel[]> {
  return get(BASE);
}

export function listActive(): Promise<RiskModel[]> {
  return get(`${BASE}/active`);
}

export function getById(id: string): Promise<RiskModelWithDetails> {
  return get(`${BASE}/${id}`);
}

export function getByIdPublic(id: string): Promise<RiskModelWithDetails> {
  return get(`${PUBLIC_BASE}/${id}`);
}

export function create(data: {
  nameI18n: LocalizedString;
  descriptionI18n?: LocalizedString;
}): Promise<RiskModel> {
  return post(BASE, data);
}

export function createFromTemplate(data: {
  template: "simple" | "standard" | "detailed";
  nameI18n: LocalizedString;
  descriptionI18n?: LocalizedString;
}): Promise<RiskModel> {
  return post(`${BASE}/from-template`, data);
}

// QL-212 — deep-copy a model with its horizons, levels, matrix, questions,
// bounds, cap and scope settings. The copy arrives inactive.
export function clone(id: string, data: CloneRiskModelRequest): Promise<RiskModel> {
  return post(`${BASE}/${id}/clone`, data satisfies CloneRiskModelRequest);
}

// Omitted fields stay unchanged; the payload shape is owned by @repo/shared/types.
export function update(id: string, data: UpdateRiskModelRequest): Promise<UpdateRiskModelResponse> {
  return patch(`${BASE}/${id}`, data satisfies UpdateRiskModelRequest);
}

export function toggleActive(id: string): Promise<{ success: boolean }> {
  return post(`${BASE}/${id}/toggle-active`, {});
}

export function checkDelete(id: string): Promise<DeleteCheckResponse> {
  return get<DeleteCheckResponse>(`${BASE}/${id}/delete-check`);
}

export function remove(id: string): Promise<{ success: boolean }> {
  return del(`${BASE}/${id}`);
}

// --- Time Horizons ---

// QP-180 — cards-mode extras editable per option row.
type RiskOptionPayload = {
  labelI18n: LocalizedString;
  descriptionI18n?: LocalizedString | null;
  iconSvg?: string | null;
};

export function createTimeHorizon(
  modelId: string,
  data: RiskOptionPayload,
): Promise<{ id: string }> {
  return post(`${BASE}/${modelId}/time-horizons`, data);
}

export function updateTimeHorizon(
  thId: string,
  data: RiskOptionPayload,
): Promise<{ success: boolean }> {
  return patch(`${BASE}/time-horizons/${thId}`, data);
}

export function deleteTimeHorizon(thId: string): Promise<{ success: boolean }> {
  return del(`${BASE}/time-horizons/${thId}`);
}

// --- Risk Levels ---

export function createRiskLevel(modelId: string, data: RiskOptionPayload): Promise<{ id: string }> {
  return post(`${BASE}/${modelId}/risk-levels`, data);
}

export function updateRiskLevel(
  rlId: string,
  data: RiskOptionPayload,
): Promise<{ success: boolean }> {
  return patch(`${BASE}/risk-levels/${rlId}`, data);
}

export function deleteRiskLevel(rlId: string): Promise<{ success: boolean }> {
  return del(`${BASE}/risk-levels/${rlId}`);
}

// --- Matrix Scores ---

export function upsertMatrixScore(
  modelId: string,
  data: { timeHorizonId: string; riskLevelId: string; score: number },
): Promise<{ success: boolean }> {
  return put(`${BASE}/${modelId}/matrix-scores`, data);
}
